import React, { useState } from 'react';
import STLViewer from './STLViewer';

const STLViewerExample = () => {
  const [stlFile, setStlFile] = useState('');
  const [modelColor, setModelColor] = useState('#888888');
  const [autoRotate, setAutoRotate] = useState(false);
  const [autoRotateAxis, setAutoRotateAxis] = useState('y');
  const [backgroundColor, setBackgroundColor] = useState('transparent');
  const [initialRotationX, setInitialRotationX] = useState(0);
  const [initialRotationY, setInitialRotationY] = useState(0);
  const [initialRotationZ, setInitialRotationZ] = useState(0);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.stl')) {
      const url = URL.createObjectURL(file);
      setStlFile(url);
    } else {
      alert('Please select a valid STL file');
    }
  };

  const sampleSTLs = [
    { name: 'Cube', url: '/models/cube.stl' },
    { name: 'Sphere', url: '/models/sphere.stl' },
    { name: 'Teapot', url: '/models/teapot.stl' }
  ];

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h2>STL Viewer Example</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <h3>Load STL File</h3>
        <input 
          type="file" 
          accept=".stl" 
          onChange={handleFileChange}
          style={{ marginBottom: '10px' }}
        />
        
        <div>
          <h4>Or try a sample:</h4>
          {sampleSTLs.map((sample, index) => (
            <button 
              key={index}
              onClick={() => setStlFile(sample.url)}
              style={{ 
                margin: '5px', 
                padding: '5px 10px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {sample.name}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Controls</h3>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <label>
            Model Color:
            <input 
              type="color" 
              value={modelColor} 
              onChange={(e) => setModelColor(e.target.value)}
              style={{ marginLeft: '10px' }}
            />
          </label>
          
          <label>
            Background:
            <select 
              value={backgroundColor} 
              onChange={(e) => setBackgroundColor(e.target.value)}
              style={{ marginLeft: '10px' }}
            >
              <option value="transparent">Transparent</option>
              <option value="#ffffff">White</option>
              <option value="#000000">Black</option>
              <option value="#f0f0f0">Light Gray</option>
            </select>
          </label>
          
          <label>
            <input 
              type="checkbox" 
              checked={autoRotate} 
              onChange={(e) => setAutoRotate(e.target.checked)}
            />
            Auto Rotate
          </label>
          
          {autoRotate && (
            <label>
              Rotation Axis:
              <select 
                value={autoRotateAxis} 
                onChange={(e) => setAutoRotateAxis(e.target.value)}
                style={{ marginLeft: '10px' }}
              >
                <option value="x">X-Axis</option>
                <option value="y">Y-Axis</option>
                <option value="z">Z-Axis</option>
              </select>
            </label>
          )}
        </div>
        
        <div style={{ marginTop: '15px' }}>
          <h4>Initial Rotation (radians)</h4>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <label>
              X Rotation:
              <input 
                type="range" 
                min="-3.14159" 
                max="3.14159" 
                step="0.1" 
                value={initialRotationX} 
                onChange={(e) => setInitialRotationX(parseFloat(e.target.value))}
                style={{ marginLeft: '10px', width: '100px' }}
              />
              <span style={{ marginLeft: '5px' }}>{initialRotationX.toFixed(1)}</span>
            </label>
            
            <label>
              Y Rotation:
              <input 
                type="range" 
                min="-3.14159" 
                max="3.14159" 
                step="0.1" 
                value={initialRotationY} 
                onChange={(e) => setInitialRotationY(parseFloat(e.target.value))}
                style={{ marginLeft: '10px', width: '100px' }}
              />
              <span style={{ marginLeft: '5px' }}>{initialRotationY.toFixed(1)}</span>
            </label>
            
            <label>
              Z Rotation:
              <input 
                type="range" 
                min="-3.14159" 
                max="3.14159" 
                step="0.1" 
                value={initialRotationZ} 
                onChange={(e) => setInitialRotationZ(parseFloat(e.target.value))}
                style={{ marginLeft: '10px', width: '100px' }}
              />
              <span style={{ marginLeft: '5px' }}>{initialRotationZ.toFixed(1)}</span>
            </label>
          </div>
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        flexWrap: 'wrap',
        alignItems: 'flex-start'
      }}>
        <div>
          <h3>Small Viewer (300x300)</h3>
          <STLViewer 
            stlFile={stlFile}
            width={300}
            height={300}
            backgroundColor={backgroundColor}
            modelColor={modelColor}
            autoRotate={autoRotate}
            autoRotateAxis={autoRotateAxis}
            enableMouseControls={true}
            initialRotationX={initialRotationX}
            initialRotationY={initialRotationY}
            initialRotationZ={initialRotationZ}
          />
        </div>
        
        <div>
          <h3>Large Viewer (500x400)</h3>
          <STLViewer 
            stlFile={stlFile}
            width={500}
            height={400}
            backgroundColor={backgroundColor}
            modelColor={modelColor}
            autoRotate={autoRotate}
            autoRotateAxis={autoRotateAxis}
            enableMouseControls={true}
            lightIntensity={1.2}
            initialRotationX={initialRotationX}
            initialRotationY={initialRotationY}
            initialRotationZ={initialRotationZ}
          />
        </div>
      </div>

      <div style={{ marginTop: '30px' }}>
        <h3>Usage</h3>
        <pre style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '15px', 
          borderRadius: '4px',
          overflow: 'auto'
        }}>
{`import STLViewer from './components/STLViewer';

<STLViewer 
  stlFile="/path/to/model.stl"
  width={400}
  height={400}
  backgroundColor="transparent"
  modelColor="#888888"
  autoRotate={false}
  autoRotateAxis="y"
  rotationSpeed={0.01}
  enableMouseControls={true}
  lightIntensity={1.0}
  cameraDistance={5}
  initialRotationX={0}
  initialRotationY={1.57}
  initialRotationZ={0}
/>`}
        </pre>
      </div>
    </div>
  );
};

export default STLViewerExample;
